import { useEffect, useMemo, useState } from 'react';

import { Dialog, DialogContent, Grid, Typography } from '@mui/material';

import { navigate } from '@redwoodjs/router';
import { MetaTags, useMutation, useQuery } from '@redwoodjs/web';

import AssignPartnersModal from 'src/components/AssignPartnersModal';
import Loader from 'src/components/Loader/Loader';
import ModalContent from 'src/components/ModalContent/ModalContent';
import TableList from 'src/components/TableList/TableList';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { PRICING_OFFER_TABLE_HEADERS } from 'src/constants';
import { useAuthContext } from 'src/contexts/AuthProvider';
import {
    ASSIGN_TRANSACTION_PRICING_OFFER_FOR_PARTNERS,
    ASSIGN_TRANSACTION_PRICING_OFFER_FOR_PARTNERS_OF_PARTNER,
} from 'src/graphql/mutations/assignTransactionPricingOffer.mutation';
import { DELETE_TRANSACTION_PRICING_OFFER } from 'src/graphql/mutations/deleteTransactionPricingOffer.mutation';
import { GET_TRANSACTION_PRICING_SUMMARY } from 'src/graphql/queries/getTransactionPricingOffers.query';
import { usePartnerAssignment } from 'src/hooks/usePartnerAssignment/usePartnerAssignment';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import { LogError } from 'src/lib/error-logger';
import { ProductStatusChip } from 'src/utils/productUtils';
import {
    FilterOptionsType,
    filteredPricingOffersTableData,
    updateTableFilters,
} from 'src/utils/tableUtils';

const TransactionPricingPage = () => {
    const { isPartnerOfPartner, isAdmin } = useAuthContext();
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [idToDelete, setIdToDelete] = useState<number>();
    const [idToAssign, setIdToAssign] = useState<number>();
    const [tableFilters, setTableFilters] = useState({});

    const [assignedPartners, setAssignedPartners] = useState([]);
    const [assignedPartnersOfPartners, setAssignedPartnersOfPartners] = useState([]);

    const useTransactionOfferId = true;

    const { data, loading, refetch } = useQuery(GET_TRANSACTION_PRICING_SUMMARY, {
        fetchPolicy: 'network-only',
        onError: (error) => {
            showErrorToast('We were unable to load transaction summaries');
            LogError(error);
        },
    });

    const [deleteTransactionOffer] = useMutation(DELETE_TRANSACTION_PRICING_OFFER, {
        onError: (error) => {
            showErrorToast('We were unable to delete the selected transaction offer');
            LogError(error);
        },
        onCompleted: () => {
            showSuccessToast('Transaction Offer Deleted!');
            window.location.reload();
        },
    });

    const [assignOfferForPartners] = useMutation(ASSIGN_TRANSACTION_PRICING_OFFER_FOR_PARTNERS, {
        onError: (error) => {
            showErrorToast('Assignment request was unsuccessful');
            LogError(error);
        },
        onCompleted: (data) => {
            showSuccessToast('Assignment request was successful');
            window.location.reload();
        },
    });

    const [assignOfferForPartnersOfPartner] = useMutation(
        ASSIGN_TRANSACTION_PRICING_OFFER_FOR_PARTNERS_OF_PARTNER,
        {
            onError: (error) => {
                showErrorToast('Assignment request was unsuccessful');
                LogError(error);
            },
            onCompleted: (data) => {
                showSuccessToast('Assignment request was successful');
                window.location.reload();
            },
        }
    );

    const filterOptions = () => {
        const transactionPricingOffers = data?.getTransactionPricingOffers ?? [];
        return Object.fromEntries([
            [
                'Partner Name',
                Array.from(
                    new Set(transactionPricingOffers.map(({ partnerName }) => partnerName))
                ).map((partnerName) => ({ value: partnerName, label: partnerName })),
            ],
            [
                'Status',
                Array.from(new Set(transactionPricingOffers.map(({ status }) => status))).map(
                    (status) => ({ value: status, label: status })
                ),
            ],
        ]) as FilterOptionsType;
    };

    const tableData = useMemo(() => {
        return filteredPricingOffersTableData(
            data?.getTransactionPricingOffers ?? [],
            tableFilters
        ).map((transactionPricingOffer) => ({
            ...transactionPricingOffer,
            name: transactionPricingOffer.offerName,
            statusChip: <ProductStatusChip status={transactionPricingOffer.status} />,
            assignToTotal: (
                <Typography
                    onClick={() => {
                        isAdmin && handleOpenAssignModal(transactionPricingOffer.id);
                    }}
                    sx={{
                        cursor: isAdmin ? 'pointer' : 'not-allowed',
                        textDecoration: isAdmin ? 'underline' : 'none',
                    }}
                >
                    {(
                        (transactionPricingOffer.partners?.length ?? 0) +
                        (transactionPricingOffer.partnersOfPartner?.length ?? 0)
                    ).toString()}
                </Typography>
            ),
        }));
    }, [data, isPartnerOfPartner, tableFilters]);

    const exportData = useMemo(() => {
        let transactionPricingOffers = data?.getTransactionPricingOffers ?? [];

        transactionPricingOffers = transactionPricingOffers.map((el) =>
            !isPartnerOfPartner
                ? {
                      ...el,
                      statusChip: el.status,
                      name: el.offerName,
                      assignToTotal: (
                          (el?.partners ?? ([]?.length || 0)) +
                          (el?.partnersOfPartner ?? ([]?.length || 0))
                      ).toString(),
                  }
                : {
                      ...el,
                      statusChip: el.status,
                      name: el.offerName,
                  }
        );

        return transactionPricingOffers;
    }, [data, isPartnerOfPartner]);

    useEffect(() => {
        if (idToAssign && data) {
            const transactionalOffer = (data?.getTransactionPricingOffers ?? []).find(
                (el) => el.id === idToAssign
            );
            const assignedPartners = (transactionalOffer?.partners ?? []).map((partner) => ({
                value: partner.id,
                type: 'partner',
            }));
            const assignedPartnersOfPartners = (transactionalOffer?.partnersOfPartner ?? []).map(
                (partnerOfPartner) => ({
                    value: partnerOfPartner.id,
                    type: 'partnerOfPartner',
                })
            );

            setAssignedPartners(assignedPartners);
            setAssignedPartnersOfPartners(assignedPartnersOfPartners);
        }
    }, [idToAssign, data]);

    const {
        assigned,
        unassigned,
        handleAssignPartners,
        handleOpenAssignModal,
        handleCloseAssignModal,
        isAssignModalOpen,
    } = usePartnerAssignment({
        idToAssign,
        setIdToAssign,
        assignedPartners,
        assignedPartnersOfPartners,
        refetch,
        assignOfferForPartners,
        assignOfferForPartnersOfPartner,
        useTransactionOfferId,
    });

    const addNewTransactionPricingOffer = () => {
        navigate('/add-transaction-pricing');
    };
    const handleViewTransactionPricingOffer = ({ id }) => {
        navigate(`/transaction-pricing/${id}`);
    };

    // Delete record callback sent to table component
    const handleOpenDeleteModal = (recordId: number) => {
        setIdToDelete(recordId);
        setOpenDeleteModal(true);
    };

    const handleDeleteRecord = () => {
        idToDelete && deleteTransactionOffer({ variables: { id: idToDelete } });
        handleCloseDeleteModal();
    };
    // Close the delete modal
    const handleCloseDeleteModal = () => {
        setIdToDelete(undefined);
        setOpenDeleteModal(false);
    };

    return (
        <PageContent
            heading="Transaction Pricing Offers"
            subHeading="Add, View, Remove and Update Transaction Pricing Offers as necessary"
        >
            <MetaTags title="Transaction Pricing" description="Transaction Pricing Offer Page" />

            <Grid container sx={{ gap: '24px' }}>
                <Grid item xs={12}>
                    {loading ? (
                        <Loader />
                    ) : (
                        <TableList
                            addRecordButtonText="Add Transaction Offer"
                            data={tableData}
                            exportData={exportData}
                            headers={PRICING_OFFER_TABLE_HEADERS}
                            title="Online Offer List"
                            addRecordCallBack={addNewTransactionPricingOffer}
                            handleViewRecord={handleViewTransactionPricingOffer}
                            deleteRecordCallBack={handleOpenDeleteModal}
                            enableFilter
                            enableExport
                            filterOptions={filterOptions()}
                            updateTableFilters={(filter: string, newFilterValue: string) =>
                                setTableFilters((prevFilters) =>
                                    updateTableFilters(prevFilters, newFilterValue, filter)
                                )
                            }
                        />
                    )}
                </Grid>
                {isAssignModalOpen && (unassigned.length > 0 || assigned.length > 0) && (
                    <AssignPartnersModal
                        open={isAssignModalOpen}
                        onClose={handleCloseAssignModal}
                        onAssign={(assigned) => {
                            handleAssignPartners(assigned, []);
                        }}
                        onUnassign={(unassigned) => {
                            handleAssignPartners([], unassigned);
                        }}
                        assignedInitial={assigned}
                        unassignedInitial={unassigned}
                    />
                )}

                <Dialog
                    open={openDeleteModal}
                    onClose={handleCloseDeleteModal}
                    aria-labelledby="delete-transaction-offer-dialog"
                    sx={{
                        '& .MuiDialog-paper': {
                            width: '400px',
                        },
                    }}
                >
                    <DialogContent>
                        <ModalContent
                            handleClose={handleCloseDeleteModal}
                            handleAction={handleDeleteRecord}
                            header="Delete Transaction?"
                            message="Are you sure you want to permanently delete this transaction? This transaction cannot be restored once deleted"
                            buttonText="Delete"
                        />
                    </DialogContent>
                </Dialog>
            </Grid>
        </PageContent>
    );
};

export default TransactionPricingPage;
